import { getGameRuntimeConfig } from "./api/config";
import { readPreferredModelId } from "./api/models";
import { prepareGamePortalResume, flushGameBeforePortalLeave } from "./game-portal-resume";
import { resolveGamePortalBaseUrl } from "./portal-origin";
import { createId } from "./utils/crypto";
import {
  syncNativeTrustedForumOrigins,
} from "./api/runtime-endpoints";
import {
  IOS_WEBCLIP_SHELL,
  isIosWebClip,
  isNativeGameWebView,
} from "./runtime-shell";

export { isIosWebClip, isNativeGameWebView } from "./runtime-shell";

export const FORUM_AUTH_RETURN_QUERY_KEY = "game_return_to";
export const FORUM_AUTH_STATE_QUERY_KEY = "game_state";
export const FORUM_AUTH_SOURCE_QUERY_KEY = "game_auth";
export const FORUM_AUTH_SOURCE = "forum";
export const FORUM_AUTH_STATE_STORAGE_KEY = "apk-games:forum-auth-state";
export const GAME_PORTAL_ACTION_QUERY_KEY = "game_action";
export const GAME_PORTAL_PACKAGE_QUERY_KEY = "package_id";
export const GAME_PORTAL_MODEL_QUERY_KEY = "model_id";
export const GAME_PORTAL_JWT_QUERY_KEY = "jwttoken";
export const GAME_PORTAL_TARGET_QUERY_KEY = "game_portal_target";
export const GAME_PORTAL_SHELL_QUERY_KEY = "game_shell";
export const GAME_PORTAL_IOS_WEBCLIP_SHELL = IOS_WEBCLIP_SHELL;
export const GAME_PORTAL_CALLBACK_HASH_PREFIX = "#game_callback?";

const GAME_PORTAL_MAX_AGE_MS = 15 * 60 * 1000;
type AndroidGamePortalBridge = {
  openPortalInWebView?: (url: string) => void;
};

type NativeRuntimeGlobal = typeof globalThis & {
  GGEndpointBridge?: AndroidGamePortalBridge;
};

export type GamePortalTarget =
  | "auth"
  | "forum"
  | "models"
  | "membership"
  | "recharge";
export type GameForumDestination = "home" | "community";
export type GamePortalAction = "auth" | "model";
export type GamePortalReturnTarget = Exclude<
  GamePortalTarget,
  "auth" | "models"
>;

export interface ForumAuthUrlInput {
  callbackUrl: string;
  forumBaseUrl: string;
  state: string;
}

export interface GamePortalUrlInput extends ForumAuthUrlInput {
  target: GamePortalTarget;
  packageId: number;
  selectedModelId?: number;
  forumDestination?: GameForumDestination;
  preserveCallbackPath?: boolean;
  shell?: typeof GAME_PORTAL_IOS_WEBCLIP_SHELL;
}

export interface GamePortalLaunchOptions {
  target: GamePortalTarget;
  selectedModelId?: number;
  forumDestination?: GameForumDestination;
}

function resolveGamePortalLaunchOptions(
  options: GamePortalLaunchOptions,
  packageId: number,
): GamePortalLaunchOptions {
  if (options.target !== "models") return options;
  // 官网选模回传会先落入 package 级偏好；它比可能尚未同步的游戏 UI 快照更接近真实调用模型。
  const preferredModelId = readPreferredModelId(packageId);
  return preferredModelId
    ? { ...options, selectedModelId: preferredModelId }
    : options;
}

interface PendingGamePortalRequest {
  state: string;
  action: GamePortalAction;
  packageId: number;
  createdAt: number;
}

function persistPendingGamePortalRequest(pending: PendingGamePortalRequest) {
  try {
    window.sessionStorage.setItem(
      FORUM_AUTH_STATE_STORAGE_KEY,
      JSON.stringify(pending),
    );
  } catch {
    throw new Error("当前 WebView 无法保存跳转校验状态，请重启游戏后重试。");
  }
}

export interface ParsedGamePortalCallbackLocation {
  cleanPath: string;
  params: URLSearchParams;
  transport: "fragment" | "query";
}

const GAME_PORTAL_CALLBACK_KEYS = [
  GAME_PORTAL_JWT_QUERY_KEY,
  FORUM_AUTH_STATE_QUERY_KEY,
  FORUM_AUTH_SOURCE_QUERY_KEY,
  GAME_PORTAL_ACTION_QUERY_KEY,
  GAME_PORTAL_PACKAGE_QUERY_KEY,
  GAME_PORTAL_MODEL_QUERY_KEY,
];

/**
 * 新版 APK 回跳参数放在 fragment 中，确保 WebView 只向 Capacitor 本地服务请求 `/`。
 * query 分支保留给已经发出的旧版官网回跳链接，读取后同样立即清理。
 */
export function parseGamePortalCallbackLocation(
  value: string,
): ParsedGamePortalCallbackLocation | undefined {
  const url = new URL(value);
  const fragmentParams = url.hash.startsWith(GAME_PORTAL_CALLBACK_HASH_PREFIX)
    ? new URLSearchParams(
        url.hash.slice(GAME_PORTAL_CALLBACK_HASH_PREFIX.length),
      )
    : undefined;
  const params = fragmentParams ?? new URLSearchParams(url.searchParams);
  if (!GAME_PORTAL_CALLBACK_KEYS.some((key) => params.has(key)))
    return undefined;

  if (fragmentParams) {
    url.hash = "";
  } else {
    GAME_PORTAL_CALLBACK_KEYS.forEach((key) => url.searchParams.delete(key));
  }

  return {
    cleanPath: `${url.pathname}${url.search}${url.hash}`,
    params,
    transport: fragmentParams ? "fragment" : "query",
  };
}

function callbackActionForTarget(
  target: GamePortalTarget,
): GamePortalAction | undefined {
  if (target === "auth") return "auth";
  if (target === "models") return "model";
  return undefined;
}

function pathForTarget(target: GamePortalTarget) {
  if (target === "auth") return "/auth";
  return "/member";
}

function memberTabForTarget(
  target: Exclude<GamePortalTarget, "auth" | "forum">,
) {
  return target === "membership" ? "plans" : target;
}

function forumUrl(
  forumBaseUrl: string,
  destination: GameForumDestination = "home",
) {
  const base = new URL(`${forumBaseUrl.replace(/\/$/, "")}/`);
  // 悬浮窗“论坛”进入站点首页；卡片社区入口才进入论坛分组页。
  if (destination === "community" && base.pathname === "/") {
    base.pathname = "/forum/groups";
  } else if (destination === "home") {
    base.pathname = "/";
  }
  base.search = "";
  base.hash = "";
  return base.toString();
}

export function buildGamePortalUrl(input: GamePortalUrlInput) {
  const portalUrl =
    input.target === "forum"
      ? new URL(forumUrl(input.forumBaseUrl, input.forumDestination))
      : new URL(
          pathForTarget(input.target),
          `${input.forumBaseUrl.replace(/\/$/, "")}/`,
        );
  const callbackAction = callbackActionForTarget(input.target);

  // 登录和选模需要一次性回调校验；论坛、会员与充值只保存短期来源现场，不能伪装成模型回调。
  portalUrl.searchParams.set(
    FORUM_AUTH_RETURN_QUERY_KEY,
    normalizeCallbackUrl(input.callbackUrl, input.preserveCallbackPath),
  );
  portalUrl.searchParams.set(FORUM_AUTH_STATE_QUERY_KEY, input.state);
  portalUrl.searchParams.set(
    GAME_PORTAL_PACKAGE_QUERY_KEY,
    String(input.packageId),
  );
  // 门户目标始终显式传递，官网才能在认证、选模和普通浏览页统一维持返回游戏入口。
  portalUrl.searchParams.set(GAME_PORTAL_TARGET_QUERY_KEY, input.target);
  if (input.shell) {
    // 官网只凭显式 Web Clip 外壳标识展示网页返回按钮，Android APK 继续由原生覆盖层负责。
    portalUrl.searchParams.set(GAME_PORTAL_SHELL_QUERY_KEY, input.shell);
  }
  if (callbackAction) {
    portalUrl.searchParams.set(GAME_PORTAL_ACTION_QUERY_KEY, callbackAction);
  }

  if (input.target !== "auth" && input.target !== "forum") {
    portalUrl.searchParams.set("tab", memberTabForTarget(input.target));
  }
  if (
    input.target === "models" &&
    input.selectedModelId &&
    input.selectedModelId > 0
  ) {
    portalUrl.searchParams.set(
      GAME_PORTAL_MODEL_QUERY_KEY,
      String(input.selectedModelId),
    );
  }
  return portalUrl.toString();
}

export function buildForumAuthUrl(input: ForumAuthUrlInput) {
  const portalUrl = new URL(
    "/auth",
    `${input.forumBaseUrl.replace(/\/$/, "")}/`,
  );
  portalUrl.searchParams.set(
    FORUM_AUTH_RETURN_QUERY_KEY,
    normalizeCallbackUrl(input.callbackUrl),
  );
  portalUrl.searchParams.set(FORUM_AUTH_STATE_QUERY_KEY, input.state);
  portalUrl.searchParams.set(GAME_PORTAL_ACTION_QUERY_KEY, "auth");
  return portalUrl.toString();
}

export function createGamePortalLaunchUrl(options: GamePortalLaunchOptions) {
  if (typeof window === "undefined") return getGameRuntimeConfig().forumBaseUrl;

  const config = getGameRuntimeConfig();
  const portalBaseUrl = resolveGamePortalBaseUrl(config.forumBaseUrl);
  const launchOptions = resolveGamePortalLaunchOptions(
    options,
    config.packageId,
  );
  const state = createId().replaceAll("-", "");
  const isWebClip = isIosWebClip();
  // iOS Web Clip 直接回到离开前的同源游戏地址；APK 固定回 Capacitor 根路径，再由短期现场恢复页面。
  const preserveCallbackPath = isWebClip;
  // 最终构造时再按容器归一化：iOS 保留游戏路径，Android 收敛到 Capacitor 根路径。
  const callbackUrl = window.location.href;
  const callbackAction = callbackActionForTarget(launchOptions.target);
  if (callbackAction) {
    persistPendingGamePortalRequest({
      state,
      action: callbackAction,
      packageId: config.packageId,
      createdAt: Date.now(),
    });
  }

  return buildGamePortalUrl({
    callbackUrl,
    forumBaseUrl: portalBaseUrl,
    forumDestination: launchOptions.forumDestination,
    packageId: config.packageId,
    preserveCallbackPath,
    selectedModelId: launchOptions.selectedModelId,
    shell: isWebClip ? GAME_PORTAL_IOS_WEBCLIP_SHELL : undefined,
    state,
    target: launchOptions.target,
  });
}

export function createForumAuthLaunchUrl() {
  return createGamePortalLaunchUrl({ target: "auth" });
}

export function openGamePortal(options: GamePortalLaunchOptions) {
  if (typeof window === "undefined") return;
  const config = getGameRuntimeConfig();
  const launchOptions = resolveGamePortalLaunchOptions(
    options,
    config.packageId,
  );
  const isNative = isNativeGameWebView();
  const isWebClip = isIosWebClip();
  if (isNative || isWebClip) {
    const launch = () => {
    // 受控容器在当前顶层页面进入官网；离开前保存现场，返回时才能稳定恢复游戏。
    prepareGamePortalResume(config.packageId);
    if (isNative) syncNativeTrustedForumOrigins();
    const launchUrl = createGamePortalLaunchUrl(launchOptions);
    if (isNative && openPortalInNativeWebView(launchUrl)) return;
    window.location.assign(launchUrl);
    };
    try {
      const pending = flushGameBeforePortalLeave();
      if (pending) void pending.then(launch).catch(() => window.alert("离开前的进度未能保存，请稍后重试。"));
      else launch();
    } catch { window.alert("离开前的进度未能保存，请稍后重试。"); }
    return;
  }

  // Web 游戏与当前官网共享 origin 和登录存储；不得跟随 API 故障切换跳到另一域名。
  const portalBaseUrl = resolveGamePortalBaseUrl(config.forumBaseUrl);
  window.open(
    createBrowserPortalUrl(launchOptions, portalBaseUrl, config.packageId),
    "_blank",
    "noopener,noreferrer",
  );
}

function openPortalInNativeWebView(url: string) {
  const bridge = (globalThis as NativeRuntimeGlobal).GGEndpointBridge;
  if (typeof bridge?.openPortalInWebView !== "function") return false;
  try {
    bridge.openPortalInWebView(url);
    return true;
  } catch {
    // 旧版或异常原生桥退回当前 window 导航，仍不得改用 window.open。
    return false;
  }
}

function createBrowserPortalUrl(
  options: GamePortalLaunchOptions,
  forumBaseUrl: string,
  packageId: number,
) {
  if (options.target === "forum") {
    return forumUrl(forumBaseUrl, options.forumDestination);
  }

  const url = new URL(
    pathForTarget(options.target),
    `${forumBaseUrl.replace(/\/$/, "")}/`,
  );
  if (options.target === "auth") return url.toString();

  url.searchParams.set("tab", memberTabForTarget(options.target));
  url.searchParams.set(GAME_PORTAL_PACKAGE_QUERY_KEY, String(packageId));
  if (options.selectedModelId && options.selectedModelId > 0) {
    url.searchParams.set(
      GAME_PORTAL_MODEL_QUERY_KEY,
      String(options.selectedModelId),
    );
  }
  return url.toString();
}

export function consumeForumAuthState(
  receivedState: string,
  storage?: Pick<Storage, "getItem" | "removeItem">,
) {
  return consumeGamePortalState(receivedState, undefined, undefined, storage);
}

export function consumeGamePortalState(
  receivedState: string,
  receivedAction?: string,
  receivedPackageId?: number,
  storage?: Pick<Storage, "getItem" | "removeItem">,
) {
  const stateStorage =
    storage ??
    (typeof window !== "undefined" ? window.sessionStorage : undefined);
  if (!stateStorage) return false;

  try {
    const raw = stateStorage.getItem(FORUM_AUTH_STATE_STORAGE_KEY) ?? "";
    stateStorage.removeItem(FORUM_AUTH_STATE_STORAGE_KEY);
    if (!raw || !receivedState) return false;

    // 兼容已经发出的旧版登录请求；新版请求同时校验业务类型、packageId 与有效期。
    if (!raw.startsWith("{")) return raw === receivedState;
    const pending = JSON.parse(raw) as Partial<PendingGamePortalRequest>;
    if (pending.state !== receivedState) return false;
    if (
      !pending.createdAt ||
      Date.now() - pending.createdAt > GAME_PORTAL_MAX_AGE_MS
    )
      return false;
    if (pending.action !== receivedAction) return false;
    if (pending.packageId !== receivedPackageId) return false;
    return true;
  } catch {
    return false;
  }
}

function normalizeCallbackUrl(value: string, preservePath = false) {
  const callbackUrl = new URL(value);
  if (preservePath) {
    GAME_PORTAL_CALLBACK_KEYS.forEach((key) =>
      callbackUrl.searchParams.delete(key),
    );
    callbackUrl.searchParams.delete(GAME_PORTAL_TARGET_QUERY_KEY);
    if (callbackUrl.hash.startsWith(GAME_PORTAL_CALLBACK_HASH_PREFIX))
      callbackUrl.hash = "";
  } else {
    callbackUrl.pathname = "/";
    callbackUrl.search = "";
    callbackUrl.hash = "";
  }
  return callbackUrl.toString();
}
